import { CheckCircle2, ChevronDown, Plus } from 'lucide-react';
import { useState } from 'react';
import { Spinner } from '@/components/ui';
import { Button } from '@/components/ui';
import { ModalContentWrapper } from '@/components/ui';
import { useLoan } from '@/hooks/use-loans';
import {
  useAddTrancheCondition,
  useApproveTranche,
  useCancelTranche,
  useCompleteTrancheCondition,
  useCreateTranche,
  useDrawTranche,
  useDrawdownSummary,
  useTranches,
} from '@/hooks/use-tranches';
import { handleMutationError } from '@/lib/error-handling';
import { formatCurrency } from '@/lib/format';
import { isLineOfCredit } from '@/lib/loan-type-labels';

interface TrancheTabProps {
  loanId: string;
}

export function TrancheTab({ loanId }: TrancheTabProps) {
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({ amount: '', purpose: '', scheduledDate: '' });
  const [formErrors, setFormErrors] = useState<{
    amount?: string;
    scheduledDate?: string;
    general?: string;
  }>({});
  const [expandedTranche, setExpandedTranche] = useState<string | null>(null);

  const { data: loan } = useLoan(loanId);
  const { data: tranches = [], isLoading } = useTranches(loanId);
  const { data: summary } = useDrawdownSummary(loanId);
  const createMutation = useCreateTranche();
  const approveMutation = useApproveTranche();
  const drawMutation = useDrawTranche();
  const cancelMutation = useCancelTranche();
  const _addConditionMutation = useAddTrancheCondition();
  const completeConditionMutation = useCompleteTrancheCondition();
  const isLoc = isLineOfCredit(loan?.loanType);
  const facilityLimit = summary?.facilityLimit ?? Number(loan?.principal ?? 0);
  const availableToDraw = summary?.availableToDraw ?? summary?.remaining ?? 0;

  function resetCreateForm() {
    setFormData({ amount: '', purpose: '', scheduledDate: '' });
    setFormErrors({});
  }

  function validateCreateTrancheForm() {
    const errors: { amount?: string; scheduledDate?: string; general?: string } = {};
    const amount = Number(formData.amount);

    if (!formData.amount.trim()) {
      errors.amount = 'Amount is required.';
    } else if (Number.isNaN(amount)) {
      errors.amount = 'Enter a valid amount.';
    } else if (amount <= 0) {
      errors.amount = 'Amount must be greater than 0.';
    } else if (amount > availableToDraw) {
      errors.amount = `Amount exceeds available to draw (${formatCurrency(availableToDraw)}).`;
    }

    if (formData.scheduledDate && !/^\d{4}-\d{2}-\d{2}$/.test(formData.scheduledDate)) {
      errors.scheduledDate = 'Use YYYY-MM-DD format.';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }

  function handleCreateTranche() {
    if (!validateCreateTrancheForm()) return;

    createMutation.mutate(
      {
        loanId,
        amount: Number(formData.amount),
        purpose: formData.purpose || undefined,
        scheduledDate: formData.scheduledDate || undefined,
      },
      {
        onSuccess: () => {
          setShowModal(false);
          resetCreateForm();
        },
        onError: (err: any) => {
          handleMutationError(err, 'Failed to create tranche');
          setFormErrors(prev => ({
            ...prev,
            general: err?.response?.data?.message
              ? Array.isArray(err.response.data.message)
                ? err.response.data.message.join(', ')
                : err.response.data.message
              : 'Failed to create tranche.',
          }));
        },
      },
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" className="w-6 h-6 text-indigo-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Drawdown Summary */}
      {summary && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Drawdown Summary</h3>
          {isLoc && (
            <p className="mb-4 text-sm text-gray-600">
              This line of credit uses the facility limit as the cap and tranches as individual
              drawdowns.
            </p>
          )}
          <div className="grid grid-cols-4 gap-4">
            <div className="bg-indigo-50 rounded-lg p-4">
              <p className="text-sm text-gray-600 mb-1">
                {isLoc ? 'Facility Limit' : 'Total Committed'}
              </p>
              <p className="text-2xl font-bold text-indigo-600">
                {formatCurrency(summary.facilityLimit ?? summary.totalCommitted)}
              </p>
            </div>
            <div className="bg-green-50 rounded-lg p-4">
              <p className="text-sm text-gray-600 mb-1">Total Drawn</p>
              <p className="text-2xl font-bold text-green-600">
                {formatCurrency(summary.totalDrawn)}
              </p>
            </div>
            <div className="bg-yellow-50 rounded-lg p-4">
              <p className="text-sm text-gray-600 mb-1">
                {isLoc ? 'Available to Allocate' : 'Remaining'}
              </p>
              <p className="text-2xl font-bold text-yellow-600">
                {formatCurrency(summary.availableToDraw ?? summary.remaining)}
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-600 mb-1">Utilisation</p>
              <p className="text-2xl font-bold text-gray-600">
                {summary.percentageUtilised.toFixed(1)}%
              </p>
            </div>
          </div>

          {/* Progress bar */}
          <div className="mt-4">
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-indigo-600 h-2 rounded-full"
                style={{ width: `${Math.min(summary.percentageUtilised, 100)}%` }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Tranches list */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Tranches</h3>
          <Button
            variant="primary"
            onClick={() => {
              resetCreateForm();
              setShowModal(true);
            }}
            className="w-fit flex items-center gap-2 px-3 py-2 text-white rounded-lg hover: text-sm font-medium"
          >
            <Plus className="w-4 h-4" />
            Add Tranche
          </Button>
        </div>

        {tranches.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500">No tranches yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {tranches.map(tranche => (
              <div key={tranche.id} className="border border-gray-200 rounded-lg overflow-hidden">
                {/* Tranche header */}
                <button
                  onClick={() =>
                    setExpandedTranche(expandedTranche === tranche.id ? null : tranche.id)
                  }
                  className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex-1 text-left">
                    <div className="flex items-center gap-3">
                      <div className="text-sm font-semibold text-gray-900">
                        Tranche #{tranche.number}
                      </div>
                      <div
                        className={`px-2 py-0.5 rounded text-xs font-medium ${
                          tranche.status === 'DRAWN'
                            ? 'bg-green-100 text-green-800'
                            : tranche.status === 'APPROVED'
                              ? 'bg-blue-100 text-blue-800'
                              : tranche.status === 'SCHEDULED'
                                ? 'bg-yellow-100 text-yellow-800'
                                : tranche.status === 'CANCELLED'
                                  ? 'bg-red-100 text-red-800'
                                  : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {tranche.status}
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      Amount:{' '}
                      <span className="font-semibold">
                        {formatCurrency(parseFloat(tranche.amount))}
                      </span>
                    </p>
                    {tranche.purpose && (
                      <p className="text-sm text-gray-500">Purpose: {tranche.purpose}</p>
                    )}
                  </div>
                  <ChevronDown
                    className={`w-5 h-5 text-gray-400 transition-transform ${
                      expandedTranche === tranche.id ? 'rotate-180' : ''
                    }`}
                  />
                </button>

                {/* Tranche details */}
                {expandedTranche === tranche.id && (
                  <div className="border-t border-gray-200 bg-gray-50 p-4 space-y-4">
                    {/* Dates */}
                    <div className="grid grid-cols-2 gap-4">
                      {tranche.scheduledDate && (
                        <div>
                          <p className="text-xs text-gray-600 mb-1">Scheduled Date</p>
                          <p className="text-sm font-medium text-gray-900">
                            {new Date(tranche.scheduledDate).toLocaleDateString()}
                          </p>
                        </div>
                      )}
                      {tranche.drawnDate && (
                        <div>
                          <p className="text-xs text-gray-600 mb-1">Drawn Date</p>
                          <p className="text-sm font-medium text-gray-900">
                            {new Date(tranche.drawnDate).toLocaleDateString()}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Conditions */}
                    {tranche.conditions && tranche.conditions.length > 0 && (
                      <div>
                        <p className="text-sm font-semibold text-gray-900 mb-2">Conditions</p>
                        <div className="space-y-2">
                          {tranche.conditions.map(condition => (
                            <div
                              key={condition.id}
                              className="flex items-start gap-2 p-2 bg-white rounded"
                            >
                              <button
                                onClick={() =>
                                  completeConditionMutation.mutate({
                                    loanId,
                                    trancheId: tranche.id,
                                    conditionId: condition.id,
                                  })
                                }
                                className={`mt-0.5 ${
                                  condition.completed
                                    ? 'text-green-600'
                                    : 'text-gray-400 hover:text-green-600'
                                }`}
                              >
                                <CheckCircle2 className="w-4 h-4" />
                              </button>
                              <div className="flex-1">
                                <p
                                  className={`text-sm ${
                                    condition.completed
                                      ? 'text-gray-500 line-through'
                                      : 'text-gray-700'
                                  }`}
                                >
                                  {condition.description}
                                </p>
                                {condition.note && (
                                  <p className="text-xs text-gray-500 mt-1">{condition.note}</p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex gap-2 pt-2 border-t border-gray-200">
                      {(tranche.status === 'PLANNED' || tranche.status === 'SCHEDULED') && (
                        <button
                          onClick={() => approveMutation.mutate({ loanId, trancheId: tranche.id })}
                          disabled={approveMutation.isPending}
                          className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50"
                        >
                          Approve
                        </button>
                      )}
                      {tranche.status === 'APPROVED' && (
                        <button
                          onClick={() => drawMutation.mutate({ loanId, trancheId: tranche.id })}
                          disabled={drawMutation.isPending}
                          className="px-3 py-1.5 bg-green-600 text-white text-sm rounded hover:bg-green-700 disabled:opacity-50"
                        >
                          Draw
                        </button>
                      )}
                      {tranche.status !== 'DRAWN' && tranche.status !== 'CANCELLED' && (
                        <button
                          onClick={() => cancelMutation.mutate({ loanId, trancheId: tranche.id })}
                          disabled={cancelMutation.isPending}
                          className="px-3 py-1.5 bg-red-600 text-white text-sm rounded hover:bg-red-700 disabled:opacity-50"
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create modal */}
      {showModal && (
        <ModalContentWrapper
          open={showModal}
          onClose={() => setShowModal(false)}
          title="Add New Tranche"
        >
          <div className="space-y-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Amount *</label>
              <input
                type="number"
                min="0.01"
                step="0.01"
                value={formData.amount}
                onChange={e => {
                  setFormData(prev => ({ ...prev, amount: e.target.value }));
                  setFormErrors(prev => ({ ...prev, amount: undefined, general: undefined }));
                }}
                placeholder="0.00"
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                  formErrors.amount ? 'border-red-300 bg-red-50' : 'border-gray-300'
                }`}
              />
              <div className="mt-1 flex items-center justify-between text-xs text-gray-500">
                <span>Facility limit: {formatCurrency(facilityLimit)}</span>
                <span>Available to allocate: {formatCurrency(availableToDraw)}</span>
              </div>
              {formErrors.amount && (
                <p className="mt-1 text-sm text-red-600">{formErrors.amount}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Purpose</label>
              <input
                type="text"
                value={formData.purpose}
                onChange={e => {
                  setFormData(prev => ({ ...prev, purpose: e.target.value }));
                  setFormErrors(prev => ({ ...prev, general: undefined }));
                }}
                placeholder="e.g., Equipment purchase"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Scheduled Date</label>
              <input
                type="date"
                value={formData.scheduledDate}
                onChange={e => {
                  setFormData(prev => ({ ...prev, scheduledDate: e.target.value }));
                  setFormErrors(prev => ({
                    ...prev,
                    scheduledDate: undefined,
                    general: undefined,
                  }));
                }}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                  formErrors.scheduledDate ? 'border-red-300 bg-red-50' : 'border-gray-300'
                }`}
              />
              <p className="mt-1 text-xs text-gray-500">
                Optional. If set, this must be a valid calendar date in `YYYY-MM-DD` format.
              </p>
              {formErrors.scheduledDate && (
                <p className="mt-1 text-sm text-red-600">{formErrors.scheduledDate}</p>
              )}
            </div>
          </div>
          {formErrors.general && (
            <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {formErrors.general}
            </div>
          )}
          <div className="flex gap-3">
            <Button
              variant="secondary"
              onClick={() => {
                setShowModal(false);
                resetCreateForm();
              }}
              className="w-fit"
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleCreateTranche}
              disabled={!formData.amount || createMutation.isPending || availableToDraw <= 0}
              className="w-fit"
            >
              {createMutation.isPending ? 'Creating...' : 'Create'}
            </Button>
          </div>
        </ModalContentWrapper>
      )}
    </div>
  );
}
